import * as React from "react";

import Coin from "@svg/billing/Coin";
import ReferralIconBackground from "@svg/referral-icon-background";
import ReferralPanelBackground from "@svg/referral-panel-background";
import ReferralPanelOverlay from "@svg/referral-panel-overlay";

import { Button } from "@replo/design-system/components/button/Button";
import { useSidebar } from "@replo/design-system/components/sidebar/Sidebar";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { skipToken } from "@tanstack/react-query";
import {
  formatAffiliateClicks,
  formatAffiliateEarnings,
} from "@v2/features/dub/utils/affiliates";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { affiliatesRouter } from "@v2/trpc/routes/affiliates";
import { routes } from "@v2/utils/router";
import { ArrowRight, Link, MousePointerClick } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

export const ReferralPanel = () => {
  const { user } = useCurrentUser();
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();
  const { open } = useSidebar();
  const isSidebarExpanded = open;

  const { data, error } = (affiliatesRouter as any).getAnalytics.useQuery(
    user ? {} : skipToken,
  );

  React.useEffect(() => {
    if (error) {
      console.error("[ReferralPanel] Error fetching analytics:", error);
    }
  }, [error]);

  if (!user) {
    return null;
  }

  const clickCount = data?.clickCount ?? 0;
  const earningsInDollars = data?.earningsInDollars ?? 0;

  const handleReferralsClick = () => {
    if (projectId) {
      navigate(
        generatePath(routes.shops.settings.referrals, {
          projectId,
        }),
      );
    }
  };

  if (!isSidebarExpanded) {
    return (
      <Tooltip content="My Referrals" triggerAsChild>
        <button
          type="button"
          onClick={handleReferralsClick}
          className="relative flex items-center justify-center w-8 h-8 cursor-pointer rounded-md overflow-hidden border-0.5 border-border bg-white"
        >
          <ReferralIconBackground
            className="absolute"
            style={{
              width: "32px",
              height: "32px",
            }}
          />
          <Coin className="relative w-4 h-4" />
        </button>
      </Tooltip>
    );
  }

  return (
    <div className="relative w-full aspect-square rounded-xl overflow-hidden border-0.5 border-border">
      <ReferralPanelBackground className="absolute inset-0 w-full h-full" />
      <ReferralPanelOverlay className="absolute inset-0 w-full h-full" />

      <div className="absolute top-0 left-0 right-0 pt-3 text-center">
        <span className="header-h6 text-default">Earn with Referrals</span>
      </div>

      <div
        className="absolute flex items-center justify-between rounded-lg bg-white/50 border-[3px] border-white/50 backdrop-blur-[1px] px-2 py-1"
        style={{ top: "24%", left: "-3.5%", width: "88%", height: "14%" }}
      >
        <div className="flex items-center gap-1 pl-1">
          <Link size={10} />
          <span className="text-[11px] font-medium">Your links</span>
        </div>
        <div className="flex items-center gap-0.5">
          <MousePointerClick size={8} />
          <span className="text-2xs text-muted font-normal">
            {formatAffiliateClicks(clickCount)}
          </span>
        </div>
      </div>

      <div
        className="absolute flex items-center gap-2 rounded-lg bg-white/50 border-[4px] border-white/50 backdrop-blur-[1px] p-2"
        style={{ top: "45.5%", left: "17%", width: "88%", height: "24%" }}
      >
        <Coin className="w-6 h-7 -rotate-[8deg] shrink-0" />
        <div className="flex flex-col">
          <span className="text-2xs text-muted font-normal">
            Monthly Earnings
          </span>
          <span className="header-h6 text-default">
            {formatAffiliateEarnings(earningsInDollars)}
          </span>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 px-3 pb-3">
        <Button
          variant="primary"
          size="sm"
          onClick={handleReferralsClick}
          endEnhancer={<ArrowRight />}
          className="w-full"
        >
          {earningsInDollars > 0 ? "View Earnings" : "Share & Earn"}
        </Button>
      </div>
    </div>
  );
};
